import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, Button, Pill } from "../common/Primitives";
import { Coins, ShoppingCart, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

function formatINR(v) {
  const n = Number(v || 0);
  return `₹${n.toLocaleString("en-IN")}`;
}

/**
 * Investor DaaS purchase widget for a given startup. Renders on the investor
 * scope of the Startup Passport. Applies tier-aware pricing computed by the
 * backend on `/api/investor/daas/quote`.
 */
export default function InvestorDaaSStore({ startupId, startupName }) {
  const [catalog, setCatalog] = useState({ modules: [], packages: [] });
  const [tier, setTier] = useState(null);
  const [selectedModuleIds, setSelectedModuleIds] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [quote, setQuote] = useState(null);
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/daas/catalog").then((r) => setCatalog(r.data)).catch(() => {}),
      api.get("/investor/daas/tier").then((r) => setTier(r.data)).catch(() => {}),
    ]);
  }, []);

  useEffect(() => {
    const hasSelection = selectedPackageId || (selectedModuleIds || []).length > 0;
    if (!hasSelection) { setQuote(null); return; }
    api.post("/investor/daas/quote", {
      startup_id: startupId,
      module_ids: selectedPackageId ? [] : selectedModuleIds,
      package_id: selectedPackageId,
    })
      .then((r) => setQuote(r.data))
      .catch((e) => {
        setQuote(null);
        toast.error(formatApiError(e));
      });
  }, [startupId, selectedModuleIds, selectedPackageId]);

  const toggleModule = (mid) => {
    if (selectedPackageId) setSelectedPackageId(null);
    setSelectedModuleIds((cur) =>
      cur.includes(mid) ? cur.filter((x) => x !== mid) : [...cur, mid],
    );
  };

  const selectPackage = (pid) => {
    setSelectedModuleIds([]);
    setSelectedPackageId((cur) => (cur === pid ? null : pid));
  };

  const placeOrder = async () => {
    setPlacing(true);
    try {
      const r = await api.post("/investor/daas/orders", {
        startup_id: startupId,
        module_ids: selectedPackageId ? [] : selectedModuleIds,
        package_id: selectedPackageId,
      });
      toast.success(`Order placed · ${r.data.order_id}`);
      setSelectedModuleIds([]);
      setSelectedPackageId(null);
      setQuote(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPlacing(false);
    }
  };

  const canPlaceOrder = !!quote && (selectedPackageId || selectedModuleIds.length > 0);

  return (
    <Card testId="investor-daas-store" className="mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="overline flex items-center gap-1"><Coins size={12} /> DILIGENCE STORE</div>
          <div className="font-heading text-ink text-lg mt-1">
            Purchase diligence on <span className="text-gold">{startupName || "this startup"}</span>
          </div>
          <div className="text-xs text-ink-muted mt-1">
            Pick any modules à-la-carte, or a bundle. Prices reflect your investor tier.
          </div>
        </div>
        {tier ? (
          <Pill tone="gold" testId="investor-daas-tier-pill">
            Tier {tier.tier} · {tier.discount_pct}% off
          </Pill>
        ) : null}
      </div>

      {/* Packages */}
      <div className="grid md:grid-cols-3 gap-3 mb-5">
        {(catalog.packages || []).map((p) => (
          <button
            type="button"
            key={p.package_id}
            onClick={() => selectPackage(p.package_id)}
            className={`text-left rounded border px-4 py-3 transition ${
              selectedPackageId === p.package_id
                ? "border-gold bg-gold/10"
                : "border-navy/15 bg-white/70 hover:border-gold/50"
            }`}
            data-testid={`investor-daas-package-${p.package_id}`}
          >
            <div className="flex items-center justify-between">
              <div className="font-heading text-ink text-sm">{p.name}</div>
              {p.badge ? <Pill tone="gold">{p.badge}</Pill> : null}
            </div>
            <div className="text-[11px] text-ink-muted mt-1">{p.description}</div>
            <div className="font-mono text-ink text-sm mt-2">
              {formatINR(p.price_inr)}{" "}
              {p.list_price_inr > p.price_inr && (
                <span className="text-ink-muted line-through text-xs ml-1">
                  {formatINR(p.list_price_inr)}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Modules */}
      <div className="grid md:grid-cols-2 gap-2 mb-5">
        {(catalog.modules || []).map((m) => {
          const checked = selectedModuleIds.includes(m.module_id) && !selectedPackageId;
          return (
            <label
              key={m.module_id}
              className={`flex items-start gap-3 rounded border px-3 py-2 cursor-pointer transition ${
                checked ? "border-gold bg-gold/5" : "border-navy/10 bg-white/70 hover:border-gold/40"
              }`}
              data-testid={`investor-daas-module-${m.module_id}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleModule(m.module_id)}
                className="mt-1"
                data-testid={`investor-daas-module-check-${m.module_id}`}
              />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-heading text-ink text-sm">{m.name}</div>
                  <div className="font-mono text-ink text-sm">{formatINR(m.price_inr)}</div>
                </div>
                <div className="text-[11px] text-ink-muted mt-0.5">{m.description}</div>
              </div>
            </label>
          );
        })}
      </div>

      {/* Quote summary */}
      {quote && (
        <div className="border border-gold/40 bg-gold/[0.05] rounded p-4 flex flex-wrap items-center justify-between gap-3" data-testid="investor-daas-quote">
          <div>
            <div className="overline">YOUR PRICE</div>
            <div className="font-heading text-2xl text-ink mt-1" data-testid="investor-daas-final-inr">
              {formatINR(quote.final_inr)}
            </div>
            <div className="text-[11px] text-ink-muted font-mono mt-1">
              Base {formatINR(quote.list_price_inr)}
              {quote.tier_discount_inr > 0
                ? ` · Tier ${quote.investor_tier} discount −${formatINR(quote.tier_discount_inr)} (${quote.tier_discount_pct}%)`
                : ""}
            </div>
          </div>
          <Button
            onClick={placeOrder}
            disabled={placing || !canPlaceOrder}
            data-testid="investor-daas-place-order-btn"
          >
            <ShoppingCart size={14} className="mr-1" />
            {placing ? "Placing…" : "Place Order"}
          </Button>
        </div>
      )}

      <div className="mt-3 text-[10px] font-mono text-ink-faint flex items-center gap-1">
        <ShieldCheck size={11} /> All orders are reviewed by VenturaZ analysts before delivery. Payments are currently in mock mode.
      </div>
    </Card>
  );
}
